/**
 * The register (`KLQ-20260821-001` §A, §B, §D) — the primary surface at every project size.
 *
 * FOUR DECISIONS THIS FILE IS BUILT ON
 *
 * 1. **One flat, server-filtered list**, newest number first, over the keyset cursor. Not a
 *    tree with counts: §A records why the wireframe's grouping was declined, and the short
 *    version is that a group inside a paginated list is complete only when every one of its
 *    rows is loaded, so the first page renders four buildings that each look half-empty and
 *    are silently wrong. The tree narrows this list instead — through the screen's slicer rail
 *    (`TasksPage`), which writes the `under=` this list is fetched with — and the day the
 *    ancestor-rollup counters exist, this component is the thing that gets grouped.
 * 2. **No virtualisation library, and no page cap either** (`KLQ-20260910-001` §F). The cap
 *    was the stand-in: `maxPages: 5` bounded the DOM at 250 rows, and paid for it by dropping
 *    the oldest page over a cursor with no way back — scroll far enough and task #1 was gone,
 *    which the register had to admit in a sentence under the list. Both are gone. The rows
 *    arrive on the scroll (`lib/infinite.ts`) and only the visible ones are drawn
 *    (`lib/windowing.ts`, the same window `TreeTable` has used since `KLQ-20260820-005`), so
 *    the list is complete and the DOM is still bounded — by what fits on screen rather than
 *    by a constant, and still with no dependency.
 * 3. **No number here claims to be a total** (§D). Every count on this screen goes through
 *    `LoadedCount`, which carries a `+` while the cursor has more to give. There is no counts
 *    endpoint and this wave does not add one; the `+` is the difference between a fact and a
 *    guess.
 * 4. **The empty states are two different sentences, and the difference is load-bearing**
 *    (C7). A project with no tasks gets the persona's own invitation; a filter that matches
 *    nothing gets *Clear filters*. Printing the hero block over a filtered register would tell
 *    somebody their project is empty when what is empty is their question.
 *
 * Nothing here decides who may do what (§F). The persona the empty state addresses is the
 * server's own `role` off the surface matrix, which is a choice of *wording*, not of access.
 */

import { useState } from "react";

import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CircleHelp, Diamond, MapPin, TriangleAlert } from "lucide-react";

import {
  Button,
  ChipSelect,
  EmptyState,
  InlineError,
  Pill,
  Select,
  SkeletonRows,
} from "@/components";
import type { PillTone } from "@/components";
import { membersQueryOptions, partiesQueryOptions } from "@/features/people";
import type { ProjectMember } from "@/features/people";
import {
  projectQueryOptions,
  relativeTime,
  useProjectSurfaces,
} from "@/features/projects";
import { useActiveWorkspaceId } from "@/features/session";
import type { MessageKeys } from "@/lib/i18n";
import { useLocale, useMessages } from "@/lib/i18n";
import { useInfiniteSentinel } from "@/lib/infinite";
import { useAnchoredWindow } from "@/lib/windowing";
import type { Translate } from "@/lib/i18n";

import { TASK_STATUSES } from "./api";
import type { Task, TaskKind, TaskPriority, TaskStatus } from "./api";
import type { TaskFilters } from "./filters";
import { isNarrowed, NO_FILTERS } from "./filters";
import type { FirmOnTask } from "./firms";
import { firmOf, OffSystemMark } from "./firms";
import { KIND_KEY } from "./kinds";
import { LoadedCount } from "./LoadedCount";
import { bulkSkipKey, detailOf, taskErrorKey } from "./problems";
import { SliceBar } from "./SliceBar";
import { TASK_TYPE_ICONS } from "./taskTypeIcons";
import {
  renderPersonName,
  renderPersonNames,
  useBulkEditTasks,
  useExportTasks,
  useMemberNames,
  useNodeNames,
  useProjectLabels,
  useTaskList,
  useTaskTransition,
  useTaskTypes,
} from "./queries";
// UX cleanup 2026-08-25 — `Create task` is drawn once, by `TasksPage`, for every view.
// import { TaskCreateDrawer } from "./TaskCreateDrawer";
import { useTaskFilterNavigation } from "./TaskFilterBar";
import styles from "./Tasks.module.css";

type Key = MessageKeys["tasks"];

/**
 * One register row, in pixels. TWIN: `--register-row-h` in `Tasks.module.css`.
 *
 * The window needs a number, and the stylesheet needs the same number; the two are stated in
 * both places because a row measured at render time is a row that resizes the list while
 * somebody is reading it. A row is a title line and a meta line, each clipped to one line, in
 * `--space-3` of padding.
 */
const REGISTER_ROW_HEIGHT = 60;

export const STATUS_KEY: Readonly<Record<TaskStatus, Key>> = {
  open: "status.open",
  in_progress: "status.inProgress",
  done: "status.done",
  closed: "status.closed",
};

const PRIORITY_KEY: Readonly<Record<TaskPriority, Key>> = {
  high: "priority.high",
  normal: "priority.normal",
  low: "priority.low",
};

/**
 * Status to its pill tone (`STYLE.md` §10). chat1.md §5.2's ramp, as the four tones the one
 * `Pill` has rather than as four classes of this feature's own.
 *
 * *Open* is the lowest-urgency state and gets the lowest contrast, so the amber of work in
 * flight is the only colour that shouts; *closed* is `neutral` for the same reason, and the
 * word beside it is what tells the two apart. Exported with the two key maps above for the
 * root `/tasks` page's rows (`MyTaskRow`), which draw the same pill and must not grow a third
 * copy of either map.
 */
export const STATUS_TONE: Readonly<Record<TaskStatus, PillTone>> = {
  open: "info",
  in_progress: "warning",
  done: "success",
  closed: "neutral",
};

/**
 * C7's three glyphs: ⚠ issue, ◇ task, ? question.
 *
 * One family (Lucide, 1.5px stroke) like every other icon in the app, and coloured with
 * `--pin-task` — the same hue the drawing viewer will give a task pin, so the glyph in the
 * register and the pin on the sheet are recognisably the same thing. Exported for the board's
 * cards, which are the same rows in a different arrangement.
 */
export function KindGlyph({
  kind,
  label,
  taskTypeId = null,
}: {
  readonly kind: TaskKind;
  readonly label: string;
  /**
   * The studio's own type for this task, when it has one (`KLQ-20260825-005`). Its glyph and
   * colour win over the kind's, because *"Skontrolovať"* is what the architect actually
   * wants to pick out of a list — the kind is the fallback for every task raised before the
   * vocabulary existed, or by hand without one.
   */
  readonly taskTypeId?: string | null;
}) {
  /**
   * **One cache entry, not one fetch per row.** `useTaskTypes` reads the vocabulary the
   * session already holds under the workspace's key, so N rows share one request and one
   * answer; what a row costs is a subscription, not a round trip. Resolving here rather than
   * threading a prop through four screens is what keeps the register, the board, the plan
   * view and the detail drawing a type the same way.
   */
  const types = useTaskTypes();
  const type =
    taskTypeId === null || taskTypeId === undefined
      ? undefined
      : types.data?.items.find((row) => row.id === taskTypeId);

  const Icon =
    type !== undefined
      ? TASK_TYPE_ICONS[type.icon]
      : kind === "issue"
        ? TriangleAlert
        : kind === "question"
          ? CircleHelp
          : Diamond;
  // `role="img"` beside the label: an `<svg>` carrying an `aria-label` and no role is not
  // reliably announced, and this glyph is the only thing on the row that says what it is.
  return (
    <Icon
      className={styles.glyph}
      size={16}
      strokeWidth={1.5}
      role="img"
      aria-label={type?.name ?? label}
      // The colour is a token name off the wire, read as a custom property rather than as a
      // hex (`CLAUDE.md`; `scripts/guard.mjs` enforces it for `src/`).
      {...(type === undefined ? {} : { style: { color: `var(--${type.color})` } })}
    />
  );
}

/**
 * Which of C7's empty sentences a project with no tasks gets.
 *
 * The role is the server's — `ProjectSurfacesResponse.role`, the caller's effective role after
 * the workspace/membership composition — so this is not the client deciding anything (§F). It
 * is choosing which true sentence to print, and the alternative is one sentence that is wrong
 * for three of the four people who read it: telling a contractor to *"capture your first
 * task"* when raising one is not their job, or telling an architect their site is quiet when
 * the register is the surface they live on.
 */
function emptyKeys(role: string | undefined): {
  readonly title: Key;
  readonly body: Key;
} {
  switch (role) {
    case "contractor":
      return { title: "empty.contractor.title", body: "empty.contractor.body" };
    case "client_readonly":
      return { title: "empty.client.title", body: "empty.client.body" };
    case "engineer":
      return { title: "empty.engineer.title", body: "empty.engineer.body" };
    default:
      return { title: "empty.architect.title", body: "empty.architect.body" };
  }
}

export interface TaskRegisterProps {
  readonly projectId: string;
  readonly filters: TaskFilters;
}

export function TaskRegister({ projectId, filters }: TaskRegisterProps) {
  const t = useMessages("tasks");
  const { locale } = useLocale();
  const list = useTaskList(projectId, filters, "register");
  const nameOf = useMemberNames(projectId);
  // The same cache entry `useMemberNames` reads one line up, and the project the shell already
  // holds: the row's assignee picker costs no request of its own.
  const members = useQuery(membersQueryOptions(projectId));
  // The firm each row is owed by, off the one roster read — never a request per row.
  const parties = useQuery(partiesQueryOptions(projectId));
  const project = useQuery(projectQueryOptions(projectId));
  const myWorkspaceId = useActiveWorkspaceId();
  const nodeNameOf = useNodeNames(projectId);
  const labels = useProjectLabels(projectId);
  const surfaces = useProjectSurfaces(projectId);
  const exporting = useExportTasks(projectId);
  const apply = useTaskFilterNavigation(projectId, "register");
  // const [creating, setCreating] = useState(false);
  // The element the observer watches, held as state rather than a ref: a ref's `.current`
  // changing does not re-render, so the effect that observes it would never see it arrive.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

  const tasks = list.data?.pages.flatMap((page) => page.items) ?? [];
  // The **last page's** `has_more`, not `hasNextPage`: they agree today and the criterion is
  // written against the wire field, which is the one the server promises.
  const hasMore = list.data?.pages.at(-1)?.has_more ?? false;
  const narrowed = isNarrowed(filters);
  // What the slice leaves out, off the envelope of the page this component already holds
  // (`KLQ-20260820-005` phase 1). `?? null` and never `?? 0`: before the first page lands
  // there is no number, and printing a zero would say the slice hides nothing.
  const excludedBySlice = list.data?.pages.at(-1)?.excluded_by_slice ?? null;
  useInfiniteSentinel(sentinel, {
    hasNextPage: list.hasNextPage,
    isFetchingNextPage: list.isFetchingNextPage,
    fetchNextPage: () => {
      void list.fetchNextPage();
    },
  });

  /**
   * Which rows are in the document — `TreeTable`'s window, on this list (§F.3).
   *
   * The cap that used to bound the DOM is gone, so a person 600 rows deep would otherwise have
   * 600 rows of markup. `useAnchoredWindow` needs a **fixed row height**, which is why
   * `.list > li` states one and both lines of a row are clipped to one line: a register row is
   * a table row that has not admitted it yet.
   */
  const view = useAnchoredWindow(tasks.length, REGISTER_ROW_HEIGHT);
  const drawn = tasks.slice(view.window.first, view.window.last);

  /**
   * Who a row's picker may offer (`MCK-20260521-008`, the assignee half of the inline-picker
   * criterion) — computed once for the list, not once per row.
   *
   * **Only people whose assignment admits nobody.** Assigning somebody at another firm lets
   * that firm into the task, and the detail screen states that before it does it
   * (`TaskDetail.tsx`, decision 4). A 60-pixel row has no room for that sentence, so the row
   * does not offer the act: a member with no claimed firm, or one at the firm that owns the
   * project, is offered; everyone else stays assignable from the detail screen. When this
   * caller cannot know who owns the project (a shared project — the payload says `shared`
   * and names no owner), only members with no firm at all qualify, because a firm this
   * screen cannot place might be the one the assignment lets in.
   *
   * `null` when the member list cannot be read — `read_people`'s floor is `architect`, so an
   * engineer or a contractor is refused it (`useMemberNames` explains the gap) — and then the
   * row prints the names as text rather than a picker full of *Name unavailable* chips.
   */
  const owningWorkspaceId =
    project.data?.relation === "owned" ? myWorkspaceId : null;
  const assignable: readonly ProjectMember[] | null = members.isError
    ? null
    : (members.data?.items ?? []).filter(
        (member) =>
          member.company_workspace_id == null ||
          member.company_workspace_id === owningWorkspaceId,
      );

  const persona = emptyKeys(surfaces.data?.role);
  const labelName = (id: string): string | null =>
    labels.data?.items.find((label) => label.id === id)?.name ?? null;

  return (
    <div className={styles.register}>
      {/* The filter bar is drawn once by `TasksPage`, into `Screen`'s `toolbar` slot
          (`KLQ-20260910-001` §A). It stood here and in the plan and in the board, three
          copies with identical wiring, all three inside the region that scrolls. */}
      <div className={styles.registerHead}>
        {list.isSuccess ? (
          <LoadedCount count={tasks.length} hasMore={hasMore} />
        ) : (
          <span />
        )}
        <div className={styles.headActions}>
          {/* The filter object currently on screen, and never a second copy of it: the export
              and the list both go through `taskQuery()` (`api.ts`), which is what makes
              "export the current filter" structurally true rather than maintained. */}
          <Button
            size="sm"
            loading={exporting.isPending}
            disabled={!list.isSuccess}
            onClick={() => {
              exporting.mutate(filters);
            }}
          >
            {t("export.action")}
          </Button>
          {/* UX cleanup 2026-08-25 — one Create for the screen, in the header.
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              setCreating(true);
            }}
          >
            {t("create.action")}
          </Button>
          */}
        </div>
      </div>

      {/* §A: which part of the project is being read, above the list rather than inside a
          control. `under=` narrows within the caller's zone scope and can never widen it, so
          clearing it restores the project-wide register and nothing more. One chip per
          subtree since `KLQ-20260820-005` §2 — the rail holds several, and a sentence naming
          two of them could only be undone all at once. */}
      <SliceBar
        under={filters.under}
        nameOf={nodeNameOf}
        excluded={excludedBySlice}
        onChange={(under) => {
          apply({ ...filters, under: [...under] });
        }}
      />

      {exporting.isError ? (
        <InlineError variant="block">
          {detailOf(exporting.error) ?? t(taskErrorKey(exporting.error))}
        </InlineError>
      ) : null}

      {list.isError ? (
        <InlineError variant="block">
          {detailOf(list.error) ?? t(taskErrorKey(list.error))}
        </InlineError>
      ) : list.isPending ? (
        // The row's own geometry, at the row's own height: the glyph, the title, the pill.
        // Four rows rather than a page's worth — a placeholder is a guess about content, and
        // the empty state below would have to walk back any guess that came out too high.
        <SkeletonRows
          label={t("status.loading")}
          rows={4}
          rowHeight={60}
          columns={[16, "34%", 64]}
        />
      ) : tasks.length === 0 ? (
        narrowed ? (
          <EmptyState
            variant="list"
            explains={t("empty.explains")}
            title={t("empty.filtered.title")}
            body={t("empty.filtered.body")}
            action={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  apply(NO_FILTERS);
                }}
              >
                {t("filter.clear")}
              </Button>
            }
          />
        ) : (
          <EmptyState
            variant="list"
            explains={t("empty.explains")}
            title={t(persona.title)}
            body={t(persona.body)}
          />
        )
      ) : (
        <ol className={styles.list} ref={view.ref}>
          {/* The height of the rows above the window, so the scrollbar is the length of the
              whole list rather than of the part being drawn. */}
          {view.window.above > 0 ? (
            <li aria-hidden style={{ height: `${String(view.window.above)}px` }} />
          ) : null}
          {drawn.map((task) => (
            <li key={task.id}>
              <TaskRow
                task={task}
                t={t}
                locale={locale}
                assignee={renderPersonNames(
                  t,
                  nameOf,
                  task.assignee_user_ids,
                  t("row.unassigned"),
                )}
                nameOf={nameOf}
                assignable={assignable}
                firm={firmOf(parties.data, task.assignee_party_id)}
                location={nodeNameOf(task.hierarchy_node_id)}
                labelNames={task.label_ids
                  .map(labelName)
                  .filter((name) => name !== null)}
              />
            </li>
          ))}
          {view.window.below > 0 ? (
            <li aria-hidden style={{ height: `${String(view.window.below)}px` }} />
          ) : null}
        </ol>
      )}

      {/* The next page arrives on the scroll (`KLQ-20260910-001` §F.2). The button stays as
          the fallback and is not decoration: a list that can only be advanced by scrolling
          cannot be finished by somebody on a keyboard or a screen reader. The sentence about
          a 250-row cap that used to sit under it is gone with the cap — every page is kept
          now, so scrolling back to the top shows task #1 again. */}
      {list.hasNextPage ? (
        <div ref={setSentinel} className={styles.sentinel}>
          <Button
            size="sm"
            disabled={list.isFetchingNextPage}
            onClick={() => {
              void list.fetchNextPage();
            }}
          >
            {list.isFetchingNextPage ? t("list.loadingMore") : t("list.more")}
          </Button>
        </div>
      ) : null}

      {/* Mounting is opening (the Drawer contract), so every open starts a blank form. The
          second entrance rides in on `locationId`: a register narrowed to a node hands that
          node to the drawer as the pre-filled location. */}
      {/* UX cleanup 2026-08-25 — hoisted to `TasksPage`, wiring unchanged.
      {creating ? (
        <TaskCreateDrawer
          projectId={projectId}
          locationId={filters.under[0] ?? null}
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
      */}
    </div>
  );
}

/**
 * One row: what it is, what it is called, who owes it, when, and where.
 *
 * Reads nothing the payload does not carry (§G). The assignee arrives already rendered,
 * because the em dash has exactly one printer and it is `renderPersonName` — a row that
 * re-derived it would be the call site that forgets the rule. That text is what a closed row
 * and a caller who cannot read the member list see; everyone else gets the picker, whose chips
 * go through the same printer.
 */
function TaskRow({
  task,
  t,
  locale,
  assignee,
  nameOf,
  assignable,
  firm,
  location,
  labelNames,
}: {
  readonly task: Task;
  readonly t: Translate<"tasks">;
  readonly locale: string;
  readonly assignee: string;
  readonly nameOf: ReturnType<typeof useMemberNames>;
  /** Who the picker may offer — see `TaskRegister`. `null`: the member list is unreadable. */
  readonly assignable: readonly ProjectMember[] | null;
  /** The firm this task is owed by, or `null` for none — see `firms.tsx`. */
  readonly firm: FirmOnTask | null;
  readonly location: string | null;
  readonly labelNames: readonly string[];
}) {
  const transition = useTaskTransition(task.project_id, task.id);
  /**
   * The assignee picker writes through bulk-edit, one task at a time (owner, 2026-09-15): a row
   * carries no ETag, so `useUpdateTask`'s `If-Match` write is not available here, and a
   * **delta** — add this person, remove that one — is what makes a write without a validator
   * safe. Two people editing the same row's assignees each change only the person they named.
   */
  const bulkEdit = useBulkEditTasks(task.project_id);
  // A `200` that applied nothing is a refusal (skip-and-report): the task closed under the
  // person, or this caller has no authority on it. It is rendered like any other refusal.
  const refused = bulkEdit.isSuccess && bulkEdit.data.applied.length === 0;
  // The chosen change is shown while it is in flight; a refusal falls back to the list's own
  // value. **Unlike the status picker, the row does not keep showing it after success**, and
  // does not need to: `useBulkEditTasks` invalidates the lists with the default refetch and
  // awaits it inside `onSuccess`, so the mutation is still pending until the register holds
  // the server's answer. Holding the delta past that point would hide a later edit by
  // somebody else — re-adding the person this row just removed — until the row remounted.
  const delta = bulkEdit.variables?.assignees;
  const heldIds: readonly string[] =
    !bulkEdit.isPending || delta == null
      ? task.assignee_user_ids
      : [
          ...task.assignee_user_ids.filter(
            (userId) => !(delta.remove ?? []).includes(userId),
          ),
          ...(delta.add ?? []).filter(
            (userId) => !task.assignee_user_ids.includes(userId),
          ),
        ];
  const assign = (assignees: { add: string[] } | { remove: string[] }): void => {
    bulkEdit.mutate({ task_ids: [task.id], assignees, clear_due_at: false });
  };
  const rowError: string | null = transition.isError
    ? t(taskErrorKey(transition.error))
    : bulkEdit.isError
      ? (detailOf(bulkEdit.error) ?? t(taskErrorKey(bulkEdit.error)))
      : refused
        ? t(bulkSkipKey(bulkEdit.data.skipped[0]?.code ?? ""))
        : null;
  // `useTaskTransition` marks the lists stale without refetching them (`refetchType: "none"`,
  // written for the detail page), so `task.status` still holds the old value after a
  // successful change. The row shows what the person chose while the change is in flight and
  // once it has landed, and falls back to the list's value when the server refused it.
  const shown: TaskStatus =
    transition.isError || transition.variables === undefined
      ? task.status
      : transition.variables;
  // A closed task is not edited from a row — the status pill's rule, for the assignees too.
  const picking = shown !== "closed" && assignable !== null;
  // A firm with nobody on it reads as the firm, not as *Unassigned* next to a firm's name —
  // the two are different problems (`filters.ts`, `SAVED_FILTERS`).
  const personText =
    picking || (firm !== null && task.assignee_user_ids.length === 0)
      ? null
      : `${assignee} · `;

  return (
    <article className={styles.row}>
      <KindGlyph
        kind={task.kind}
        label={t(KIND_KEY[task.kind])}
        taskTypeId={task.task_type_id}
      />
      <div className={styles.rowBody}>
        <p className={styles.rowTitle}>
          {/* The row's one navigation: C8 is where the task is read, moved and argued about,
              and a register whose rows lead nowhere would leave the detail reachable only by
              typing an id. The title is the link rather than the row, so the status pill and
              the meta stay selectable text. */}
          <Link
            to="/projects/$projectId/tasks/$taskId"
            params={{ projectId: task.project_id, taskId: task.id }}
            className={styles.rowLink}
          >
            <span className={styles.number}>
              {t("row.number", { number: task.number })}
            </span>{" "}
            {task.title}
          </Link>
        </p>
        {/* A refused change replaces the meta line rather than adding one under the row: the
            register is a window with a fixed row height (`REGISTER_ROW_HEIGHT`), and a third
            line would push every row below it out of step with the scrollbar. The assignee
            picker's refusals take the same place, for the same reason. */}
        {rowError !== null ? (
          <div className={styles.rowMeta}>
            <InlineError>{rowError}</InlineError>
          </div>
        ) : (
          <p className={styles.rowMeta}>
            {personText}
            {firm === null ? null : (
              <>
                {firm.name ?? t("assign.firmUnknown")}
                {firm.offSystem ? (
                  <>
                    {" "}
                    <OffSystemMark t={t} />
                  </>
                ) : null}
                {" · "}
              </>
            )}
            {task.due_at === null
              ? t("row.noDue")
              : t("row.due", { when: relativeTime(task.due_at, locale) })}
            {location === null ? "" : ` · ${location}`}
            {task.priority === "normal"
              ? ""
              : ` · ${t(PRIORITY_KEY[task.priority])}`}
            {labelNames.length === 0 ? "" : ` · ${labelNames.join(", ")}`}
          </p>
        )}
      </div>
      {/* The picker is its own column, not a piece of the meta line: a `ChipSelect` is a 32px
          control and the meta line is one 12px line of a 60px row, so a picker inside it
          would either be cut in half or make the row taller than the window believes it is.
          Beside the status `Select`, the two controls share one height and one centre line.
          `singleLine` keeps a task with four people on it from wrapping to a second row of
          chips. */}
      {picking ? (
        <div className={styles.rowAssignees}>
          <ChipSelect
            size="sm"
            singleLine
            tokens={heldIds.map((userId) => ({
              id: userId,
              label: renderPersonName(t, nameOf(userId), t("person.unavailable")),
            }))}
            options={assignable
              .filter((member) => !heldIds.includes(member.user_id))
              .map((member) => ({
                id: member.user_id,
                label: member.display_name ?? member.email ?? t("person.unavailable"),
              }))}
            addLabel={t("assign.add")}
            removeLabel={(name) => t("assign.remove", { name })}
            disabled={bulkEdit.isPending}
            onAdd={(userId) => {
              assign({ add: [userId] });
            }}
            onRemove={(userId) => {
              assign({ remove: [userId] });
            }}
          />
        </div>
      ) : null}
      {task.anchor === null ? null : (
        <MapPin
          className={styles.glyph}
          size={14}
          strokeWidth={1.5}
          role="img"
          aria-label={t("row.pinned")}
        />
      )}
      {shown === "closed" ? (
        <Pill tone={STATUS_TONE[shown]}>{t(STATUS_KEY[shown])}</Pill>
      ) : (
        <div className={styles.rowSelect}>
          <Select
            label={t("filter.status")}
            labelHidden
            size="sm"
            value={shown}
            disabled={transition.isPending}
            onChange={(event) => {
              transition.mutate(event.target.value as TaskStatus);
            }}
          >
            {TASK_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(STATUS_KEY[status])}
              </option>
            ))}
          </Select>
        </div>
      )}
    </article>
  );
}
